import React, { useState, useMemo } from "react";
import {
  Box,
  useTheme,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  Typography,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  TABLE_HEAD_BOXED_POSITIONS,
  TABLE_HEAD_BOXED_LOT,
} from "../../component/Methods";
import {
  calculateAndFormatNoAbs,
  findCust,
  findFundAbbById,
  findStrategy,
  formatValueDecimal,
} from "../../component/Methods";
import moment from "moment";

const PositionsTable = ({
  originalPositions,
  funds,
  strategies,
  custodians,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [sortField, setSortField] = useState("tickerSymbol");
  const [sortDirection, setSortDirection] = useState("asc");
  const [error, setError] = useState(null);

  // Sorting function
  const sortPositions = (positions) => {
    return [...positions].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle special cases
      if (sortField === "tickerSymbol") {
        // Special sorting for tickers: letters first, then numbers
        const aStartsWithNumber = /^\d/.test((aValue || "").toLowerCase());
        const bStartsWithNumber = /^\d/.test((bValue || "").toLowerCase());

        if (aStartsWithNumber && !bStartsWithNumber) return 1;
        if (!aStartsWithNumber && bStartsWithNumber) return -1;

        aValue = (aValue || "").toLowerCase();
        bValue = (bValue || "").toLowerCase();
      } else if (sortField === "totalShares") {
        // Numeric sorting
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (sortField === "fund") {
        // For fund, use the abbreviation
        aValue = (findFundAbbById(aValue, funds) || "").toLowerCase();
        bValue = (findFundAbbById(bValue, funds) || "").toLowerCase();
      } else if (sortField === "custodian") {
        // For custodian, use the name
        aValue = (findCust(aValue, custodians) || "").toLowerCase();
        bValue = (findCust(bValue, custodians) || "").toLowerCase();
      } else {
        // String sorting
        aValue = (aValue || "").toLowerCase();
        bValue = (bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Filter positions based on search and selections
  const filteredPositions = useMemo(() => {
    let filtered = originalPositions || [];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((item) => {
        const tickerMatch = (item.tickerSymbol || "")
          .toLowerCase()
          .includes(searchLower);
        const fundMatch = findFundAbbById(item.fund, funds)
          ?.toLowerCase()
          .includes(searchLower);
        const custodianMatch = findCust(item.custodian, custodians)
          ?.toLowerCase()
          .includes(searchLower);
        const totalSharesMatch = item.totalShares !== undefined && 
          item.totalShares !== null &&
          String(item.totalShares).includes(search);
        return tickerMatch || fundMatch || custodianMatch || totalSharesMatch;
      });
    }

    // Create a new array and sort by ticker symbol alphabetically
    const sortedFiltered = sortPositions(filtered);

    return sortedFiltered.map((position, index) => ({
      id: `${position.tickerSymbol}-${position.fund}-${index}`,
      ...position,
    }));
  }, [
    originalPositions,
    search,
    sortField,
    sortDirection,
    funds,
    custodians,
  ]);

  const toggleRow = (index) => {
    try {
      setOpenRowIndex((prevIndex) => (prevIndex === index ? null : prevIndex));
    } catch (err) {
      setError("Error toggling row expansion");
      // Error handled silently
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0); // Reset to first page when sorting
  };
  return (
    <Box sx={{ width: "100%" }}>
      {/* Filter Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search positions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>

        <Box sx={{ color: theme.palette.text.secondary, fontSize: "14px" }}>
          {filteredPositions.length} positions
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{ height: "calc(100vh - 80px)", overflowY: "scroll" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ width: 50, backgroundColor: theme.palette.grey[100] }}
              ></TableCell>
              {TABLE_HEAD_BOXED_POSITIONS.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.alignLeft ? "left" : "right"}
                  onClick={() => handleSort(headCell.id)}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    minWidth: headCell.width ? headCell.width : "auto",
                    maxWidth: 20,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": {
                      backgroundColor: theme.palette.grey[200],
                    },
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: headCell.alignLeft
                        ? "flex-start"
                        : "flex-end",
                    }}
                  >
                    {headCell.label}
                    {sortField === headCell.id && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                        }}
                      >
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!filteredPositions || !Array.isArray(filteredPositions) ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Alert severity="error">No data available</Alert>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {error && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Alert severity="error">{error}</Alert>
                    </TableCell>
                  </TableRow>
                )}
                {filteredPositions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    if (!row) return null;

                    const { tickerSymbol, fund, custodian, totalShares } = row;
                    const hasData =
                      Array.isArray(row?.lots) && row?.lots.length > 0;
                    const isOpen = openRowIndex === index;

                    return (
                      <React.Fragment key={row.id || index}>
                        <TableRow
                          sx={{
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <TableCell sx={{ width: 50 }}>
                            {hasData && (
                              <IconButton
                                size="small"
                                onClick={() => toggleRow(index)}
                                sx={{ height: "10px", width: "10px", mx: 1 }}
                              >
                                {isOpen ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {tickerSymbol
                              ? tickerSymbol.toUpperCase()
                              : "-"}
                          </TableCell>

                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {fund ? findFundAbbById(fund, funds) : "-"}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {custodian ? findCust(custodian, custodians) : "-"}
                          </TableCell>
                          <TableCell sx={{ width: "200px" }}>
                            {totalShares < 0
                              ? `(${calculateAndFormatNoAbs(
                                  Math.abs(totalShares)
                                )})`
                              : calculateAndFormatNoAbs(totalShares)}
                          </TableCell>
                        </TableRow>

                        {hasData && (
                          <TableRow>
                            <TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={5}
                            >
                              <Collapse
                                in={isOpen}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ margin: 1 }}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      mb: 2,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Lot Details
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        {TABLE_HEAD_BOXED_LOT.map(
                                          (headCell) => (
                                            <TableCell
                                              key={headCell.id}
                                              align={
                                                headCell.alignRight
                                                  ? "right"
                                                  : "left"
                                              }
                                              sx={{
                                                fontWeight: 600,
                                                backgroundColor:
                                                  theme.palette.grey[50],
                                                color:
                                                  theme.palette.text.secondary,
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                              }}
                                            >
                                              {headCell.label}
                                            </TableCell>
                                          )
                                        )}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {row.lots.map((lot, lotIndex) => (
                                        <TableRow
                                          key={lotIndex}
                                          sx={{
                                            "&:hover": {
                                              backgroundColor:
                                                theme.palette.grey[50],
                                            },
                                          }}
                                        >
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {lot.date
                                              ? moment(lot.date).format(
                                                  "MMM DD, YYYY"
                                                )
                                              : "-"}
                                          </TableCell>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {findStrategy(
                                              lot.strategy,
                                              strategies
                                            ) || "-"}
                                          </TableCell>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {findCust(
                                              lot.account?.custodian,
                                              custodians
                                            ) || "-"}
                                          </TableCell>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {lot.cost < 0
                                              ? `(${calculateAndFormatNoAbs(
                                                  Math.abs(lot.cost)
                                                )})`
                                              : calculateAndFormatNoAbs(
                                                  lot.cost
                                                )}
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {lot.shareCount < 0
                                              ? `(${calculateAndFormatNoAbs(
                                                  Math.abs(lot.shareCount)
                                                )})`
                                              : calculateAndFormatNoAbs(
                                                  lot.shareCount
                                                )}
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {lot.sharePrice < 0
                                              ? `(${formatValueDecimal(
                                                  Math.abs(lot.sharePrice)
                                                )})`
                                              : formatValueDecimal(
                                                  lot.sharePrice
                                                )}
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {lot.value < 0
                                              ? `(${calculateAndFormatNoAbs(
                                                  Math.abs(lot.value)
                                                )})`
                                              : calculateAndFormatNoAbs(
                                                  lot.value
                                                )}
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ whiteSpace: "nowrap" }}
                                          >
                                            {lot.position === 0
                                              ? "Long"
                                              : "Short"}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 500, 1000]}
        component="div"
        count={filteredPositions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.grey[100],
        }}
      />
      {/* </Box>  */}
    </Box>
  );
};

export default PositionsTable;
